import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp, getDocs } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebase';
// Assuming similar CSS can be used or create a new one if needed
import '../mahasiswa/IsiLogbookInstrumen.css';

const AdminIsiLogbook = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const [instruments, setInstruments] = useState([]); // To store list of instruments
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null); // Store admin user info
  const [logData, setLogData] = useState({
    selectedInstrumentId: '', // ID of the selected instrument
    activity: '',
    conditionBefore: '',
    conditionAfter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch admin user details
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists() && userSnap.data().role === 'admin') { // Ensure user is admin
          setUser({ uid: currentUser.uid, ...userSnap.data() });
        } else {
          setError('Akses ditolak. Hanya admin yang dapat mengisi logbook ini.');
          setUser(null);
          // Optionally redirect non-admins
          // navigate('/login');
        }
      } else {
        setError('Silakan login sebagai admin.');
        setUser(null);
        // navigate('/login');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Fetch instruments for the dropdown
  useEffect(() => {
    const fetchInstruments = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'instrumen'));
        const instrumentList = querySnapshot.docs.map(doc => ({ id: doc.id, nama: doc.data().nama }));
        setInstruments(instrumentList);
      } catch (err) {
        console.error("Error fetching instruments:", err);
        setError('Gagal memuat daftar instrumen.');
      } finally {
        setLoading(false);
      }
    };
    fetchInstruments();
  }, []);


  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLogData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Data admin tidak tersedia.');
      return;
    }
    if (!logData.selectedInstrumentId || !logData.activity || !logData.conditionBefore || !logData.conditionAfter) {
      setError('Semua field wajib diisi, termasuk memilih instrumen.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const selectedInstrument = instruments.find(inst => inst.id === logData.selectedInstrumentId);

    try {
      await addDoc(collection(db, 'logbook_instrumen'), {
        instrumentId: logData.selectedInstrumentId,
        instrumentName: selectedInstrument?.nama || 'N/A', // Get name from fetched list
        userId: user.uid, // Admin's UID
        userName: user.nama || user.email || 'Admin N/A', // Admin's name or email
        userRole: 'admin', // Indicate entry by admin
        activity: logData.activity,
        conditionBefore: logData.conditionBefore,
        conditionAfter: logData.conditionAfter,
        logTimestamp: serverTimestamp(), // Record submission time
        // No date/time start/end fields for admin
      });
      alert('Logbook berhasil disimpan oleh Admin!');
      // Clear form or navigate away
      setLogData({
        selectedInstrumentId: '',
        activity: '',
        conditionBefore: '',
        conditionAfter: '',
      });
      // Optionally navigate: navigate('/admin/dashboard');
    } catch (err) {
      console.error("Error saving admin logbook:", err);
      setError(`Gagal menyimpan logbook: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !user && !instruments.length) return <div className="loading-message">Memuat data...</div>;
  if (!user && !loading) return <div className="error-message">{error || 'Silakan login sebagai admin.'}</div>; // Show login error if not loading

  return (
    <div className="logbook-form-container">
      <h2>Isi Logbook Instrumen (Admin)</h2>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="selectedInstrumentId">Pilih Instrumen:</label>
          <select
            id="selectedInstrumentId"
            name="selectedInstrumentId"
            value={logData.selectedInstrumentId}
            onChange={handleChange}
            required
          >
            <option value="" disabled>-- Pilih Instrumen --</option>
            {instruments.map(inst => (
              <option key={inst.id} value={inst.id}>{inst.nama}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="activity">Kegiatan yang Dilakukan:</label>
          <textarea
            id="activity"
            name="activity"
            value={logData.activity}
            onChange={handleChange}
            required
            rows="3"
          />
        </div>
        <div className="form-group">
          <label htmlFor="conditionBefore">Kondisi Alat Sebelum Digunakan:</label>
          <input
            type="text"
            id="conditionBefore"
            name="conditionBefore"
            value={logData.conditionBefore}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="conditionAfter">Kondisi Alat Sesudah Digunakan:</label>
          <input
            type="text"
            id="conditionAfter"
            name="conditionAfter"
            value={logData.conditionAfter}
            onChange={handleChange}
            required
          />
        </div>
        <button type="submit" disabled={isSubmitting || !user} className="submit-button">
          {isSubmitting ? 'Menyimpan...' : 'Simpan Logbook (Admin)'}
        </button>
      </form>
    </div>
  );
};

export default AdminIsiLogbook;
